import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Grid,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { fetchScenarios,
  fetchCusips,
  uploadScenarios,
  createConfig } from '../api';
import {
  useNotification,
  notifyPermissionError,
} from '@1823-partners/core';
import dayjs from 'dayjs';
import PortfolioBuilderModal from './PortfolioBuilderModal';
import { notifyUngatedFailure } from '../utils/notifyDenial';
import { envChoices } from '../utils/siteEnv';
import { SAVE_OUTCOME_UNKNOWN, serverErrorText } from '../utils/serverErrors';

const PortfolioForm = ({ 
  formData, 
  setFormData, 
  onSubmit, 
  loading, 
  error,
  isFormComplete, 
  isWaitingForConfig,
  userId,
  metricsMode,
  onMetricsModeChange,
  selectedLotIds,
  onLotIdsChange,
  availableLotIds,
  hasData
}) => {
  const { showNotification } = useNotification();
  const [availableScenarios, setAvailableScenarios] = useState([]);
  const [availableCusips, setAvailableCusips] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [cusipsLoading, setCusipsLoading] = useState(false);

  // Load scenarios when env or date changes
  useEffect(() => {
    if (!formData.env || !formData.asofdt) return;

    const loadScenarios = async () => {
      setScenariosLoading(true);

      try {
        const scenariosResponse = await fetchScenarios(formData.env, formData.asofdt);
        setAvailableScenarios(scenariosResponse.data || []);
      } catch (error) {
        console.error('Error fetching scenarios:', error);
        notifyUngatedFailure(showNotification, error);
        setAvailableScenarios([]);
      } finally {
        setScenariosLoading(false);
      }
    };

    // Debounce API calls by 600ms to improve performance
    const timeoutId = setTimeout(loadScenarios, 600);
    return () => clearTimeout(timeoutId);
  }, [formData.env, formData.asofdt, showNotification]);

  // Load CUSIPs when env, date, or scenarios change
  useEffect(() => {
    if (!formData.env || !formData.asofdt) return;

    const loadCusips = async () => {
      setCusipsLoading(true);

      try {
        // Fetch CUSIPs - they will be filtered based on selected scenarios on the backend
        const cusipsResponse = await fetchCusips(formData.env, formData.asofdt);
        setAvailableCusips(cusipsResponse.data || []);
      } catch (error) {
        console.error('Error fetching CUSIPs:', error);
        notifyUngatedFailure(showNotification, error);
        setAvailableCusips([]);
      } finally {
        setCusipsLoading(false);
      }
    };

    // Debounce API calls by 600ms to improve performance, especially for large CUSIP lists
    const timeoutId = setTimeout(loadCusips, 600);
    return () => clearTimeout(timeoutId);
  }, [formData.env, formData.asofdt, formData.scenarios, showNotification]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log('Field changed:', name, value);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field-specific errors
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.cusips || formData.cusips.length === 0) {
      errors.cusips = 'At least one CUSIP is required';
    }
    
    if (!formData.asofdt) {
      errors.asofdt = 'As of date is required';
    }
    
    if (!formData.scenarios || formData.scenarios.length === 0) {
      errors.scenarios = 'At least one scenario is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      console.log('Form validation failed for save');
      return;
    }

    setSaveLoading(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      const configData = {
        SCEN_IDS: formData.scenarios.join(','),
        CUSIPS: formData.cusips.join(','),
        ASOFDT: dayjs(formData.asofdt).format('YYYY-MM-DD'),
        ENV: formData.env,
        DESCRIPTION: formData.description,
        ADCO_CSHF_BOND: Boolean(formData.adcoCshfBond),
        ADCO_CSHF_LOAN: Boolean(formData.adcoCshfLoan),
        SAVE: true,
        MODIFIED_BY: userId,
        // Concurrency token: the loaded MODIFIED_DATE for an edit, null for a new config.
        MODIFIED_DATE: formData.id ? formData.modifiedDate ?? null : null,
        mode: formData.id ? 'edit' : 'create'
      };

      // Include ID if it exists (for updates)
      if (formData.id) {
        configData.ID = formData.id;
      }

      const response = await createConfig(configData);
      
      // A new config takes the returned ID; either way the next save of this config
      // must send the MODIFIED_DATE this save stamped.
      const savedId = formData.id || response.data?.configId;
      if (savedId) {
        setFormData(prev => ({
          ...prev,
          id: savedId,
          modifiedDate: response.data?.modified_date ?? prev.modifiedDate,
        }));
      }

      setSaveSuccess(formData.id ? 'Configuration updated successfully!' : 'Configuration saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(null), 3000);
      
    } catch (error) {
      console.error('Error saving config:', error);
      if (!notifyPermissionError(showNotification, error)) {
        setSaveError(serverErrorText(error, {
          fallback: 'Failed to save configuration. Please try again.',
          unknown: SAVE_OUTCOME_UNKNOWN,
        }));
        // Clear error message after 5 seconds
        setTimeout(() => setSaveError(null), 5000);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset upload states
    setUploadError(null);
    setUploadSuccess(null);
    setUploadLoading(true);

    try {
      console.log('Uploading scenarios file:', file.name);
      const response = await uploadScenarios(file, formData.env);
      console.log('Upload response:', response.data);
      
      setUploadSuccess(`Successfully uploaded ${file.name}. ${response.data.message || 'Scenarios updated.'}`);
      
      // Refresh the scenarios list after successful upload
      const scenariosResponse = await fetchScenarios(formData.env);
      setAvailableScenarios(scenariosResponse.data);
      
      // Clear the file input
      event.target.value = '';
      
    } catch (error) {
      console.error('Error uploading scenarios:', error);
      if (!notifyPermissionError(showNotification, error)) {
        setUploadError(error.response?.data?.error || 'Failed to upload scenarios file. Please try again.');
      }
    } finally {
      setUploadLoading(false);
      
      // Clear success/error messages after 5 seconds
      setTimeout(() => {
        setUploadSuccess(null);
        setUploadError(null);
      }, 5000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Submit clicked, current formData:', formData);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    try {
      const configData = {
        SCEN_IDS: formData.scenarios.join(','),
        CUSIPS: formData.cusips.join(','),
        ASOFDT: dayjs(formData.asofdt).format('YYYY-MM-DD'),
        ENV: formData.env,
        DESCRIPTION: formData.description,
        ADCO_CSHF_BOND: Boolean(formData.adcoCshfBond),
        ADCO_CSHF_LOAN: Boolean(formData.adcoCshfLoan),
        SAVE: true,
        MODIFIED_BY: userId,
        MODIFIED_DATE: formData.id ? formData.modifiedDate ?? null : null,
        mode: formData.id ? 'edit' : 'create'
      };

      // Include ID if it exists (for updates)
      if (formData.id) {
        configData.ID = formData.id;
      }

      console.log('Submitting config data:', configData);
      await onSubmit(configData);
    } catch (error) {
      console.error('Error in form submission:', error);
    }
  };

  const handleOpenPortfolioBuilder = () => {
    setPortfolioModalOpen(true);
  };

  const handleClosePortfolioBuilder = () => {
    setPortfolioModalOpen(false);
  };

  const handleSelectCusipsFromPortfolio = (selectedCusips) => {
    console.log('Selected CUSIPs from portfolio:', selectedCusips);
    setFormData(prev => ({
      ...prev,
      cusips: selectedCusips
    }));
    if (formErrors.cusips) {
      setFormErrors(prev => ({
        ...prev,
        cusips: null
      }));
    }
  };

  // Memoized filtered options for better performance
  const sortedCusips = useMemo(() => {
    return (availableCusips || []).sort();
  }, [availableCusips]);

  const sortedScenarios = useMemo(() => {
    return (availableScenarios || []).sort();
  }, [availableScenarios]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
      <Paper sx={{ 
        p: 2.5, 
        backgroundColor: '#f8f9fa',
        borderRadius: 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #e9ecef',
        maxWidth: 'fit-content',
        minWidth: 900
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600,
            color: '#2c3e50',
            letterSpacing: '0.25px'
          }}>
            Portfolio Analysis
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={metricsMode}
                  onChange={(e) => onMetricsModeChange(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500, color: metricsMode ? '#0d6efd' : '#6c757d' }}>
                  Metrics Mode
                </Typography>
              }
              sx={{ mr: 0 }}
            />
            <Typography variant="body2" sx={{ 
              color: isFormComplete ? '#28a745' : '#6c757d',
              fontWeight: 500,
              fontSize: '0.85rem'
            }}>
              {isWaitingForConfig 
                ? 'Generating results...'
                : isFormComplete 
                  ? 'Data loaded • Ready to run scenarios'
                  : 'Complete fields to load historical data'}
            </Typography>
          </Box>
        </Box>
      
      <form onSubmit={handleSubmit} noValidate onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}>
        <Grid container spacing={1}>
          {/* Single Row Layout - All inputs in one row, maximum space for dropdowns */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Portfolio name"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '& fieldset': {
                    borderColor: '#dee2e6',
                  },
                  '&:hover fieldset': {
                    borderColor: '#adb5bd',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0d6efd',
                  }
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              label="As of Date"
              name="asofdt"
              type="date"
              value={formData.asofdt || dayjs().format('YYYY-MM-DD')}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              error={!!formErrors.asofdt}
              helperText={formErrors.asofdt}
              required
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '& fieldset': {
                    borderColor: '#dee2e6',
                  },
                  '&:hover fieldset': {
                    borderColor: '#adb5bd',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0d6efd',
                  }
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Env</InputLabel>
              <Select
                name="env"
                value={formData.env}
                onChange={handleChange}
                label="Env"
                sx={{
                  backgroundColor: 'white',
                  '& fieldset': {
                    borderColor: '#dee2e6',
                  },
                  '&:hover fieldset': {
                    borderColor: '#adb5bd',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0d6efd',
                  }
                }}
              >
                {envChoices(formData.env).map(({ value, disabled }) => (
                  <MenuItem key={value} value={value} disabled={disabled}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3.75}>
            <Autocomplete
              multiple
              options={['SELECT_ALL', ...sortedCusips]}
              getOptionLabel={(option) => option === 'SELECT_ALL' ? 'Select All' : (option || '')}
              value={formData.cusips || []}
              loading={cusipsLoading}
              loadingText="Loading CUSIPs..."
              noOptionsText={cusipsLoading ? "Loading..." : "No CUSIPs available"}
              onChange={(event, newValue) => {
                console.log('CUSIPs changed:', newValue);
                
                // Check if "Select All" was clicked
                if (newValue.includes('SELECT_ALL')) {
                  // If Select All is now in the array, select everything except SELECT_ALL itself
                  setFormData(prev => ({
                    ...prev,
                    cusips: [...sortedCusips]
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    cusips: newValue
                  }));
                }
                
                if (formErrors.cusips) {
                  setFormErrors(prev => ({
                    ...prev,
                    cusips: null
                  }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="CUSIPs"
                  required
                  error={!!formErrors.cusips}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {cusipsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      '& fieldset': {
                        borderColor: '#dee2e6',
                      },
                      '&:hover fieldset': {
                        borderColor: '#adb5bd',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0d6efd',
                      }
                    }
                  }}
                />
              )}
              renderOption={(props, option, { selected }) => {
                // Special handling for Select All option
                if (option === 'SELECT_ALL') {
                  // Only show as checked if there are items AND all are selected
                  const allSelected = sortedCusips.length > 0 && 
                                     formData.cusips?.length === sortedCusips.length;
                  return (
                    <li {...props} style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={allSelected}
                      />
                      Select All
                    </li>
                  );
                }
                
                return (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </li>
                );
              }}
              disableCloseOnSelect={true}
              size="small"
              filterOptions={(options, params) => {
                // Early return if no search input - show first 100 items
                if (!params.inputValue) {
                  return options.slice(0, 101);
                }
                
                const searchLower = params.inputValue.toLowerCase();
                const filtered = [];
                
                // Always include SELECT_ALL at the top if it matches
                if ('select all'.includes(searchLower)) {
                  filtered.push('SELECT_ALL');
                }
                
                // Efficiently filter with early exit after finding 100 matches
                for (let i = 1; i < options.length && filtered.length < 101; i++) {
                  if (options[i].toLowerCase().includes(searchLower)) {
                    filtered.push(options[i]);
                  }
                }
                
                return filtered;
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3.75}>
            <Autocomplete
              multiple
              options={['SELECT_ALL', ...sortedScenarios]}
              getOptionLabel={(option) => option === 'SELECT_ALL' ? 'Select All' : (option || '')}
              value={formData.scenarios || []}
              loading={scenariosLoading}
              loadingText="Loading Scenarios..."
              noOptionsText={scenariosLoading ? "Loading..." : "No Scenarios available"}
              onChange={(event, newValue) => {
                console.log('Scenarios changed:', newValue);
                
                // Check if "Select All" was clicked
                if (newValue.includes('SELECT_ALL')) {
                  // If Select All is now in the array, select everything except SELECT_ALL itself
                  setFormData(prev => ({
                    ...prev,
                    scenarios: [...sortedScenarios]
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    scenarios: newValue
                  }));
                }
                
                if (formErrors.scenarios) {
                  setFormErrors(prev => ({
                    ...prev,
                    scenarios: null
                  }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Scenarios"
                  required
                  error={!!formErrors.scenarios}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {scenariosLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      '& fieldset': {
                        borderColor: '#dee2e6',
                      },
                      '&:hover fieldset': {
                        borderColor: '#adb5bd',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#0d6efd',
                      }
                    }
                  }}
                />
              )}
              renderOption={(props, option, { selected }) => {
                // Special handling for Select All option
                if (option === 'SELECT_ALL') {
                  // Only show as checked if there are items AND all are selected
                  const allSelected = sortedScenarios.length > 0 && 
                                     formData.scenarios?.length === sortedScenarios.length;
                  return (
                    <li {...props} style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={allSelected}
                      />
                      Select All
                    </li>
                  );
                }
                
                return (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </li>
                );
              }}
              disableCloseOnSelect={true}
              size="small"
              filterOptions={(options, params) => {
                // Early return if no search input - show first 100 items
                if (!params.inputValue) {
                  return options.slice(0, 101);
                }
                
                const searchLower = params.inputValue.toLowerCase();
                const filtered = [];
                
                // Always include SELECT_ALL at the top if it matches
                if ('select all'.includes(searchLower)) {
                  filtered.push('SELECT_ALL');
                }
                
                // Efficiently filter with early exit after finding 100 matches
                for (let i = 1; i < options.length && filtered.length < 101; i++) {
                  if (options[i].toLowerCase().includes(searchLower)) {
                    filtered.push(options[i]);
                  }
                }
                
                return filtered;
              }}
            />
          </Grid>
        </Grid>

        {/* Action Buttons Row */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={2.4}>
            <Tooltip 
              title={
                !formData.env || !formData.asofdt 
                  ? "Please fill out both Scenario Env and ASOFDT before using Portfolio Builder" 
                  : ""
              }
              arrow
              placement="top"
            >
              <span>
                <Button
                  variant="outlined"
                  onClick={handleOpenPortfolioBuilder}
                  disabled={!formData.env || !formData.asofdt}
                  fullWidth
                  size="small"
                  sx={{ 
                    textTransform: 'none',
                    borderColor: '#6c757d',
                    color: '#495057',
                    '&:hover': {
                      borderColor: '#495057',
                      backgroundColor: 'rgba(108, 117, 125, 0.04)'
                    }
                  }}
                >
                  Build Portfolio
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={2.4}>
            <Tooltip
              title={metricsMode ? "Disable Metrics Mode to save portfolio" : ""}
              arrow
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={metricsMode || saveLoading || !formData.cusips?.length || !formData.asofdt || !formData.scenarios?.length}
                  fullWidth
                  size="small"
                  sx={{ 
                    backgroundColor: '#0d6efd',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#0b5ed7'
                    }
                  }}
                >
                  {saveLoading ? 'Saving...' : (formData.id ? 'Update Portfolio' : 'Save Portfolio')}
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={2.4}>
            <input
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              id="scenarios-upload-file"
              type="file"
              onChange={handleFileUpload}
              disabled={uploadLoading}
            />
            <label htmlFor="scenarios-upload-file">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                disabled={uploadLoading}
                size="small"
                sx={{ 
                  borderColor: '#6c757d',
                  color: '#495057',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#495057',
                    backgroundColor: 'rgba(108, 117, 125, 0.04)'
                  }
                }}
              >
                {uploadLoading ? 'Uploading...' : 'Upload Scenarios'}
              </Button>
            </label>
          </Grid>
          <Grid item xs={12} sm={2.4}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading || !isFormComplete}
              size="small"
              sx={{ 
                backgroundColor: metricsMode ? '#0d6efd' : '#28a745',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: metricsMode ? '#0b5ed7' : '#218838'
                }
              }}
            >
              {loading ? 'Running...' : (metricsMode ? 'Run Metrics' : 'Run Scenarios')}
            </Button>
          </Grid>
          {hasData && availableLotIds && availableLotIds.length > 0 && (
            <Grid item xs={12} sm={2.4}>
              <Autocomplete
                multiple
                options={['SELECT_ALL', ...availableLotIds]}
                getOptionLabel={(option) => option === 'SELECT_ALL' ? 'Select All' : (option || '')}
                value={selectedLotIds || []}
                onChange={(event, newValue) => {
                  // Check if "Select All" was clicked
                  if (newValue.includes('SELECT_ALL')) {
                    onLotIdsChange([...availableLotIds]);
                  } else {
                    onLotIdsChange(newValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by LOT"
                    size="small"
                    sx={{
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: '#dee2e6',
                        },
                        '&:hover fieldset': {
                          borderColor: '#adb5bd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0d6efd',
                        }
                      }
                    }}
                  />
                )}
                renderOption={(props, option, { selected }) => {
                  // Special handling for Select All option
                  if (option === 'SELECT_ALL') {
                    const allSelected = availableLotIds.length > 0 && 
                                       selectedLotIds?.length === availableLotIds.length;
                    return (
                      <li {...props} style={{ fontWeight: 'bold', borderBottom: '1px solid #ddd' }}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          style={{ marginRight: 8 }}
                          checked={allSelected}
                        />
                        Select All
                      </li>
                    );
                  }
                  
                  return (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option}
                    </li>
                  );
                }}
                disableCloseOnSelect={true}
                size="small"
                noOptionsText="No LOT IDs available"
                filterOptions={(options, params) => {
                  if (!params.inputValue) {
                    return options.slice(0, 101);
                  }
                  
                  const searchLower = params.inputValue.toLowerCase();
                  const filtered = [];
                  
                  if ('select all'.includes(searchLower)) {
                    filtered.push('SELECT_ALL');
                  }
                  
                  for (let i = 1; i < options.length && filtered.length < 101; i++) {
                    if (options[i].toLowerCase().includes(searchLower)) {
                      filtered.push(options[i]);
                    }
                  }
                  
                  return filtered;
                }}
              />
            </Grid>
          )}
        </Grid>
        {/* Messages */}
        {(saveSuccess || saveError || uploadSuccess || uploadError) && (
          <Box sx={{ mt: 1 }}>
            {saveSuccess && (
              <Typography color="#28a745" variant="body2" sx={{ fontSize: '0.875rem' }}>
                {saveSuccess}
              </Typography>
            )}
            {saveError && (
              <Typography color="#dc3545" variant="body2" sx={{ fontSize: '0.875rem' }}>
                {saveError}
              </Typography>
            )}
            {uploadSuccess && (
              <Typography color="#28a745" variant="body2" sx={{ fontSize: '0.875rem' }}>
                {uploadSuccess}
              </Typography>
            )}
            {uploadError && (
              <Typography color="#dc3545" variant="body2" sx={{ fontSize: '0.875rem' }}>
                {uploadError}
              </Typography>
            )}
          </Box>
        )}
      </form>
      
      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
      
      {/* Portfolio Builder Modal */}
      <PortfolioBuilderModal
        open={portfolioModalOpen}
        onClose={handleClosePortfolioBuilder}
        onSelectCusips={handleSelectCusipsFromPortfolio}
        env={formData.env}
        asofdt={formData.asofdt}
        selectedCusips={formData.cusips}
      />
      </Paper>
    </Box>
  );
};

export default PortfolioForm;
